import { Button } from "@/shared/components/Button";
import { axiosInstance } from "@/shared/lib/axiosInstance";
import { getThemeClass, getThemeComponentClasses } from "@/shared/lib/themeComponentClasses";
import {
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Edit,
  Eye,
  FileText,
  Mail,
  MoreHorizontal,
  Phone,
  Search,
  Settings,
  Trash2,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { Action, SortDirection } from "./dataTableUtils";

// --- sort icon component ---------------------------------------------------
export const SortIcon: React.FC<{
  active: boolean;
  direction: SortDirection;
}> = ({ active, direction }) => {
  const tableClasses = getThemeComponentClasses().table;
  if (!active || !direction) {
    return (
      <ArrowUpDown
        className={`w-3 h-3 ${getThemeClass(tableClasses.controlTextColor, "text-theme-text-soft")}`}
      />
    );
  }
  return direction === "asc" ? (
    <ChevronUp
      className={`h-3 w-3 ${getThemeClass(tableClasses.accentTextColor, "text-theme-primary")}`}
    />
  ) : (
    <ChevronDown
      className={`h-3 w-3 ${getThemeClass(tableClasses.accentTextColor, "text-theme-primary")}`}
    />
  );
};

// --- action helpers --------------------------------------------------------
const actionIconMap: Record<string, React.ReactNode> = {
  edit: <Edit className="w-4 h-4" />,
  delete: <Trash2 className="w-4 h-4" />,
  view: <Eye className="w-4 h-4" />,
  viewDetails: <FileText className="w-4 h-4" />,
  logActivity: <Clock className="w-4 h-4" />,
  call: <Phone className="w-4 h-4" />,
  sendEmail: <Mail className="w-4 h-4" />,
  custom: <Settings className="w-4 h-4" />,
};

function getActionIcon(name: string) {
  return actionIconMap[name] || null;
}

const actionLabelMap: Record<string, string> = {
  edit: "Edit",
  delete: "Delete",
  view: "View",
  viewDetails: "View Details",
  logActivity: "Log Activity",
  call: "Call",
  sendEmail: "Send Email",
  custom: "Settings",
};

function getActionLabel(name: string) {
  return actionLabelMap[name] || name.charAt(0).toUpperCase() + name.slice(1);
}

function getActionColor(name: string) {
  switch (name) {
    case "edit":
      return "text-theme-warning-text-strong hover:bg-theme-warning-pale";
    case "delete":
      return "text-theme-danger hover:bg-theme-danger-soft";
    case "view":
    case "viewDetails":
      return "text-theme-info hover:bg-theme-info-soft";
    case "logActivity":
      return "text-theme-purple-strong hover:bg-theme-purple-pale";
    case "call":
      return "text-theme-success-strong hover:bg-theme-success-pale";
    case "sendEmail":
      return "text-theme-orange-text hover:bg-theme-warning-soft";
    case "custom":
    default:
      return "text-theme-text-muted hover:bg-theme-surface-muted";
  }
}

interface ActionsDropdownProps {
  actions: Action[];
  row: any;
  rowIdx: number;
}

export const ActionsDropdown: React.FC<ActionsDropdownProps> = ({ actions, row, rowIdx }) => {
  const tableClasses = getThemeComponentClasses().table;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, isUpwards: false });

  // 🟢 Close when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Close if click is outside the button AND outside the portal menu
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        !target.closest(".actions-portal-menu")
      ) {
        setIsOpen(false);
      }
    };

    // Close on scroll or resize so the menu doesn't float away from the button
    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // 'true' ensures we catch scrolls inside the table's overflow container
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();

      // Approximate height of dropdown (padding + roughly 36px per action)
      const dropdownHeight = actions.length * 36 + 16;
      const dropdownWidth = 160; // w-40 is 160px

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // 🟢 SMART POSITIONING LOGIC
      let topPos = rect.bottom + window.scrollY + 4; // Default: open downwards
      let isUpwards = false;

      // If not enough space below, but plenty of space above -> Open Upwards
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        topPos = rect.top + window.scrollY - dropdownHeight - 4;
        isUpwards = true;
      }

      // Align to the left edge of the button, but pull it back so it aligns with the right edge
      let leftPos = rect.right + window.scrollX - dropdownWidth;

      setPosition({
        top: topPos,
        left: leftPos,
        isUpwards,
      });
    }

    setIsOpen((prev) => !prev);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`p-1 rounded-full border ${getThemeClass(tableClasses.controlBorderColor, "border-theme-border-strong")} ${getThemeClass(tableClasses.controlBgColor, "")} hover:bg-theme-surface-soft`}
      >
        <MoreHorizontal
          className={`w-4 h-4 ${getThemeClass(tableClasses.controlTextColor, "text-theme-text-muted")}`}
        />
      </button>

      {/* 🟢 PORTAL: Renders the menu directly into the body so it NEVER gets cut off by the table container */}
      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`actions-portal-menu absolute z-[9999] w-40 rounded-lg shadow-lg border py-1 animate-fade-in ${getThemeClass(tableClasses.rowBgColor, "bg-theme-surface")} ${getThemeClass(tableClasses.borderColor, "border-theme-border")}`}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              transformOrigin: position.isUpwards ? "bottom right" : "top right",
            }}
          >
            {actions.map((action, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  if (action.handler) {
                    action.handler(row, rowIdx);
                  }
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${getActionColor(action.name)}`}
              >
                {getActionIcon(action.name)}
                {getActionLabel(action.name)}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

// --- filter/search/pagination small components -----------------------------
interface FiltersProps {
  filters: Array<{
    name: string;
    valueKey: string;
    options: { value: string | number; label: string }[];
    placeholder?: string;
    width?: number | string;
  }>;
  values: Record<string, string | number>;
  onChange: (name: string, value: string | number) => void;
  requests?: { url: string; reducer: any; method: "GET" | "POST" | "PUT" | "DELETE" }[]; // optional additional requests to make on filter change
}

export const Filters: React.FC<FiltersProps> = ({ filters, values, onChange, requests }) => {
  const tableClasses = getThemeComponentClasses().table;
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const filtersContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const resolveFilterWidth = (width?: number | string) => {
    if (typeof width === "number") return `${width}px`;
    if (typeof width === "string" && width.trim()) return width;
    return "170px";
  };

  useEffect(() => {
    if (openIndex === null) return;

    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (!filtersContainerRef.current?.contains(event.target as Node)) {
        setOpenIndex(null);
      }
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown, true);
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown, true);
  }, [openIndex]);

  useEffect(() => {
    if (!requests || requests.length === 0) return;
    requests?.forEach((req) => {
      axiosInstance[req.method.toLowerCase()](req.url, {
        page: 1,
        page_size: 5000,
        search: "",
        ordering: null,
        filters: {},
      })
        .then((res) => {
          // Assuming the response has a 'data' field that contains the relevant data for the reducer
          dispatch(req.reducer(res.data));
        })
        .catch((err) => {
          console.error(`Error fetching ${req.url}:`, err);
        });
    });
  }, []);

  return (
    <div ref={filtersContainerRef} className="contents">
      {filters.map((filter, idx) => {
        const selectedValue = values[filter.name];
        const hasSelectedValue =
          selectedValue !== "" && selectedValue !== undefined && selectedValue !== null;
        const selected = filter?.options?.find?.(
          (opt) => String(opt.value) === String(selectedValue)
        );
        const filterWidth = resolveFilterWidth(filter.width);

        return (
          <div key={idx} className="relative" style={{ width: filterWidth }}>
            {/* Label */}

            {/* Selected Box */}
            <div
              onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
              className={`flex justify-between items-center px-4 py-2.5 border rounded-xl cursor-pointer transition-all duration-200 ${getThemeClass(tableClasses.controlBgColor, "")} ${getThemeClass(tableClasses.controlBorderColor, " border border-gray-300")} hover:border-theme-accent hover:bg-theme-surface`}
            >
              <span
                title={selected ? selected.label : filter.placeholder || "Select"}
                className={`text-sm flex-1 min-w-0 truncate ${getThemeClass(tableClasses.controlTextColor, "text-theme-text-muted")}`}
              >
                {selected ? selected.label : filter.placeholder || "Select"}
              </span>

              {/* Right Icons */}
              <div className="flex items-center gap-2">
                {/* ❌ Cross Icon (only show if selected) */}
                {hasSelectedValue && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // prevents dropdown toggle
                      onChange(filter.name, ""); // clear value
                    }}
                    className="p-0.5 m-0 border-0 rounded-full shadow-none leading-none bg-theme-danger"
                    aria-label={`Clear ${filter.placeholder || filter.name} filter`}
                  >
                    <X
                      className={`w-3.5 h-3.5 ${getThemeClass(tableClasses.controlTextColor, "text-white")}`}
                    />
                  </button>
                )}

                {/* Arrow Icon */}
                <svg
                  className={`w-4 h-4 transition-transform ${getThemeClass(tableClasses.controlTextColor, "text-theme-text-soft")} ${
                    openIndex === idx ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Dropdown List */}
            {openIndex === idx && (
              <div
                className={`absolute z-50 mt-2 w-full rounded-lg shadow-lg overflow-x-hidden overflow-y-auto ${getThemeClass(tableClasses.rowBgColor, "bg-theme-surface")} ${getThemeClass(tableClasses.borderColor, "border-theme-border")}`}
                style={{ maxHeight: "264px" }}
              >
                {filter?.options?.map?.((opt) => (
                  <div
                    key={opt.value}
                    title={opt.label}
                    onClick={() => {
                      onChange(filter.name, opt.value);
                      setOpenIndex(null);
                    }}
                    className={`px-4 py-3 text-sm cursor-pointer transition truncate ${getThemeClass(tableClasses.rowTextColor, "text-theme-text-muted")} hover:bg-theme-accent-soft `}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface SearchInputProps {
  placeholder?: string;
  query: string;
  onChange: (q: string) => void;
  width?: string | number;
}

const resolveSearchInputWidth = (width?: string | number) => {
  if (typeof width === "number") return `${width}px`;
  if (typeof width === "string" && width.trim()) return width;
  return "40%";
};

export const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = "Search...",
  query,
  onChange,
  width = "40%",
}) => {
  const tableClasses = getThemeComponentClasses().table;
  const [searchInput, setSearchInput] = useState(query || "");
  return (
    <div className="relative" style={{ width: resolveSearchInputWidth(width) }}>
      <input
        type="text"
        placeholder={placeholder}
        value={searchInput}
        onChange={(e) => {
          setSearchInput(e.target.value);
          onChange(e.target.value);
        }}
        className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none hover:border-theme-accent"
      />
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
      {searchInput && (
        <button
          onClick={() => {
            setSearchInput("");
            onChange("");
          }}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

interface PaginationProps {
  page: number;
  count: number;
  onPageChange: (n: number) => void;
  // optional page size to calculate number of pages (defaults to 10)
  pageSize?: number;
  TotalCount?: number;
  selectedCount?: number;
}

// export const PaginationControls: React.FC<PaginationProps> = ({
//   page,
//   count,
//   onPageChange,
//   pageSize = 10,
//   TotalCount,
//   selectedCount = 0,
// }) => {
//   console.log("PaginationControls", page, count, pageSize);

//   if (count <= 0) return null;
//   const total = Math.ceil(count / pageSize);
//   return (
//     <div className="mt-4 flex items-center justify-between px-2">
//       {/* Left Side */}
//       <div className="flex items-center gap-2">
//         {TotalCount !== undefined && (
//           <div className="flex items-center gap-2 flex-wrap">
//             <span className="px-3 py-1 text-sm font-medium text-theme-text">
//               <span className="text-theme-text-muted">Total Records : </span>
//               <span className="text-md border border-theme-accent px-3 py-1 rounded-lg">
//                 {TotalCount}
//               </span>
//             </span>
//           </div>
//         )}

//         {selectedCount > 0 && (
//           <span className="px-3 py-1 text-sm font-medium text-theme-primary border border-theme-primary/30 bg-theme-primary/5 rounded-lg">
//             Selected : {selectedCount}
//           </span>
//         )}
//       </div>

//       {/* Right Side */}
//       <div className="flex items-center gap-2">
//         <Button
//           type="button"
//           variant="secondary"
//           disabled={page <= 1}
//           onClick={() => onPageChange(Math.max(page - 1, 1))}
//           className="!px-3 h-10 w-10"
//         >
//           <ChevronLeft className="h-4 w-4" />
//         </Button>

//         <div className="flex items-center gap-2">
//           {Array.from({ length: total }, (_, i) => i + 1)
//             .filter((p) => p >= page - 3 && p <= page + 3)
//             .map((p) => {
//               const isActive = p === page;

//               return (
//                 <Button
//                   key={p}
//                   type="button"
//                   variant={isActive ? "primary" : "secondary"}
//                   onClick={() => onPageChange(p)}
//                   className={`h-10 w-10 rounded-full p-0 font-medium transition-all duration-200 ${
//                     isActive
//                       ? "bg-theme-primary text-white shadow-md"
//                       : "border border-theme-border-strong text-theme-text hover:bg-theme-surface-soft"
//                   }`}
//                 >
//                   {p}
//                 </Button>
//               );
//             })}
//         </div>

//         <Button
//           type="button"
//           variant="secondary"
//           disabled={page >= total}
//           onClick={() => onPageChange(page + 1)}
//           className="!px-3 h-10 w-10"
//         >
//           <ChevronRight className="h-4 w-4" />
//         </Button>
//       </div>
//     </div>
//   );
// };

interface PaginationProps {
  page: number;
  count: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  TotalCount?: number;
  selectedCount?: number;
}

export const PaginationControls: React.FC<PaginationProps> = ({
  page,
  count,
  onPageChange,
  pageSize = 10,
  onPageSizeChange,
  TotalCount,
  selectedCount = 0,
}) => {
  if (count <= 0) return null;

  const tableClasses = getThemeComponentClasses().table;
  const [isPageSizeOpen, setIsPageSizeOpen] = useState(false);
  const pageSizeContainerRef = useRef<HTMLDivElement>(null);
  const showPerPage = false;

  useEffect(() => {
    if (!isPageSizeOpen) return;

    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (!pageSizeContainerRef.current?.contains(event.target as Node)) {
        setIsPageSizeOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown, true);
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown, true);
  }, [isPageSizeOpen]);

  const total = Math.ceil(count / pageSize);

  const getPages = (): (number | string)[] => {
    const pages: (number | string)[] = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
      return pages;
    }

    // Always show first page
    pages.push(1);

    if (page <= 3) {
      pages.push(2, 3, 4, "...", total);
    } else if (page >= total - 2) {
      pages.push("...", total - 3, total - 2, total - 1, total);
    } else {
      pages.push("...", page - 1, page, page + 1, "...", total);
    }

    return pages;
  };

  const pages = getPages();

  return (
    <div className="mt-4 flex items-center justify-between px-2">
      {/* Left Side */}
      <div className="flex items-center gap-2">
        {showPerPage && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-theme-text">Rows per page</span>

            <div ref={pageSizeContainerRef} className="relative">
              <button
                type="button"
                onClick={() => setIsPageSizeOpen((prev) => !prev)}
                className={`flex h-9 min-w-[88px] items-center justify-between gap-2 rounded-lg border px-3 text-sm font-medium ${getThemeClass(tableClasses.controlBorderColor, "border-theme-border-strong")} ${getThemeClass(tableClasses.controlBgColor, "bg-theme-surface")} ${getThemeClass(tableClasses.controlTextColor, "text-theme-text")} hover:bg-theme-surface-soft`}
              >
                {pageSize}
                <ChevronDown
                  className={`h-4 w-4 transition ${isPageSizeOpen ? "rotate-180" : ""}`}
                />
              </button>

              {isPageSizeOpen && (
                <div
                  className={`absolute left-0 top-11 z-20 w-full overflow-hidden rounded-lg border shadow-lg ${getThemeClass(tableClasses.rowBgColor, "bg-theme-surface")} ${getThemeClass(tableClasses.borderColor, "border-theme-border")}`}
                >
                  {[10, 20, 50, 100].map((size) => {
                    const isActive = pageSize === size;
                    return (
                      <button
                        key={size}
                        type="button"
                        onClick={() => {
                          onPageSizeChange?.(size);
                          setIsPageSizeOpen(false);
                        }}
                        className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition ${
                          isActive
                            ? "bg-theme-primary/10 text-theme-primary"
                            : "text-theme-text hover:bg-theme-surface-soft"
                        }`}
                      >
                        {size}
                        {isActive ? <span className="text-xs font-semibold">Selected</span> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {TotalCount !== undefined && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1 text-sm font-medium text-theme-text">
              <span className="text-theme-text-muted">Total Records :</span>{" "}
              <span className="rounded-lg border border-theme-accent px-3 py-1 text-md">
                {TotalCount}
              </span>
            </span>
          </div>
        )}

        {selectedCount > 0 && (
          <span className="rounded-lg border border-theme-primary/30 bg-theme-primary/5 px-3 py-1 text-sm font-medium text-theme-primary">
            Selected : {selectedCount}
          </span>
        )}
      </div>

      {/* Right Side */}
      <div className="flex items-center gap-2">
        {/* Previous */}
        <Button
          type="button"
          variant="secondary"
          disabled={page === 1}
          onClick={() => onPageChange(page - 1)}
          className="h-10 w-10 !px-3"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Page Numbers */}
        <div className="flex items-center gap-2">
          {pages.map((item, index) => {
            if (item === "...") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="flex h-10 w-10 items-center justify-center text-sm font-medium text-theme-text-muted"
                >
                  • • •
                </span>
              );
            }

            const isActive = item === page;

            return (
              <Button
                key={item}
                type="button"
                variant={isActive ? "primary" : "secondary"}
                onClick={() => onPageChange(item as number)}
                className={`h-10 w-10 rounded-full p-0 font-medium transition-all ${
                  isActive
                    ? "border-0 bg-theme-primary text-white shadow-md"
                    : "border border-theme-border-strong text-theme-text hover:bg-theme-surface-soft"
                }`}
              >
                {item}
              </Button>
            );
          })}
        </div>

        {/* Next */}
        <Button
          type="button"
          variant="secondary"
          disabled={page === total}
          onClick={() => onPageChange(page + 1)}
          className="h-10 w-10 !px-3"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
